import { useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { useTheme } from '@material-ui/core/styles';
import {
  entityPresentationApiRef,
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import { createFromTemplateRouteRef } from '@internal/backstage-plugin-hm-common';
import { CardContext } from '../lib/context';
import { useStyles } from '../lib/styles';
import { CardContextValue, CardProps, CardVariant } from '../lib/types';

export const CardRoot = ({
  entity,
  link,
  Icon,
  avatar,
  title,
  type,
  description,
  actionRoute,
  actionLabel,
  styles: externalStyles,
  variant = CardVariant.normal,
  actionable,
  withoutTheme,
  withoutTags,
  withoutLinks,
  fullHeight,
  created,
  children,
  maxTitleLength,
  maxDescriptionLength,
}: CardProps) => {
  const { getPageTheme } = useTheme();
  const apiRef = useApi(entityPresentationApiRef);
  const catalogRouteRef = useRouteRef(entityRouteRef);
  const templateRoute = useRouteRef(createFromTemplateRouteRef);

  const entityType = String(
    (entity ? (entity.spec?.type as string) : type)?.toLocaleLowerCase(),
  );
  const themeForType = getPageTheme({
    themeId: `${entityType}`,
  });

  const entityTitle = entity
    ? entity.metadata.title ?? entity.metadata.name
    : title;

  const entityRefPresentationSnapshot = entity
    ? apiRef.forEntity(entity).snapshot
    : { Icon };
  const EntityIcon =
    entityRefPresentationSnapshot.Icon === false
      ? undefined
      : entityRefPresentationSnapshot.Icon;

  const entityCatalogRoute =
    entity && catalogRouteRef
      ? catalogRouteRef(entityRouteParams(entity))
      : actionRoute;

  let entityActionRoute = actionRoute;
  if (entity?.kind.toLowerCase() === 'template' && !entityActionRoute) {
    entityActionRoute = templateRoute?.({
      templateName: entity.metadata.name,
      namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
    });
  }

  const themedStyles = useStyles({
    cardFontColor: themeForType.fontColor,
    cardBackgroundImage: themeForType.backgroundImage,
  });

  const contextValue: CardContextValue = useMemo(
    () => ({
      entity,
      link,
      avatar,
      type: entityType,
      Icon: EntityIcon,
      title: entityTitle,
      maxTitleLength,
      description,
      maxDescriptionLength,
      catalogRoute: entityCatalogRoute,
      actionRoute: entityActionRoute,
      actionLabel: actionLabel,
      themedStyles,
      externalStyles,
      themeForType,
      variant,
      actionable,
      withoutTheme,
      withoutTags,
      withoutLinks,
      fullHeight,
      created,
    }),
    [
      entity,
      link,
      avatar,
      EntityIcon,
      entityTitle,
      maxTitleLength,
      description,
      maxDescriptionLength,
      entityType,
      entityCatalogRoute,
      entityActionRoute,
      actionLabel,
      themedStyles,
      externalStyles,
      themeForType,
      variant,
      actionable,
      withoutTheme,
      withoutTags,
      withoutLinks,
      fullHeight,
      created,
    ],
  );

  return (
    <CardContext.Provider value={contextValue}>{children}</CardContext.Provider>
  );
};
